/**
 * DataCleanupPrompt — collapsible hint section shown in the Upload step.
 *
 * Provides a ready-to-copy AI prompt that users can paste into ChatGPT,
 * Claude, etc. to clean up their spreadsheet before importing.
 */

import { Check, Copy01, Stars01 } from '@untitledui/icons';
import { memo, useCallback } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

const CLEANUP_PROMPT = `I need to clean up a spreadsheet before importing it into my CRM. Help me reconcile every row against my live CRM data so I don't create duplicates, and produce clean CSVs that are ready for our import flow.

**Step 1 – Understand the schema.** Call \`get_crm_schema\` to learn the CRM's entity types and property names. You'll need these to name the CSV columns correctly.

**Step 2 – Reconcile companies.** For every company in the spreadsheet, use \`search_crm\` with \`object_type: "company"\` to check if it already exists. Try name variations – "Reckitt Benckiser" might be "Reckitt", "KPMG UK" might be "KPMG". If ambiguous, search for contacts by email domain as a tiebreaker. For every match, use \`get_crm_object\` to fetch its domain – you'll need it.

**Step 3 – Reconcile contacts.** Search by email first (most reliable), then by name. For matches, use \`get_associations\` with \`association_type: "ahoy_contact_to_company"\` to check the company association is correct. Flag mismatches.

**Step 4 – Report.** Show me:
- Companies/contacts that already exist (with domains/emails)
- Genuinely new records
- Ambiguous matches for me to decide on
- Company association mismatches
- **Important:** Flag any companies that exist in the CRM but have no domain – the import flow matches companies by domain, so these will create duplicates. I'll need to add domains to those CRM records first, or handle those rows separately.

**Step 5 – Produce clean CSVs.** After I resolve ambiguities, output one CSV per entity type:

- **Companies CSV** – columns: \`Name\`, \`Domain\`, plus any other properties from the spreadsheet. For matched companies, use the domain from the CRM (not the spreadsheet) so the import flow matches correctly. Exclude matched companies that have no domain in the CRM – list those separately for manual handling.
- **Contacts CSV** – columns: \`First Name\`, \`Last Name\`, \`Email\`, \`Job Title\`, \`Company\`, plus others. Make sure the email is exact for matched contacts. The \`Company\` column should contain the company name exactly as it appears in the CRM (or in the companies CSV for new companies) – this is how the import flow associates contacts with companies.
- **Deals CSV** (if applicable) – same pattern.

Don't combine entity types into one file. Use the property labels from \`get_crm_schema\` as column headers where possible – the import flow's mapper works best with recognizable names. Split "Full Name" into "First Name" and "Last Name". Don't put free-form notes into a company or contact column called "Summary" – notes should be a separate file if needed.

Work in batches and report progress as you go.`;

interface DataCleanupPromptProps {
  orgId: number;
}

export const DataCleanupPrompt = memo(function DataCleanupPrompt({
  orgId,
}: DataCleanupPromptProps) {
  const { copied, copy } = useClipboard();
  // The MCP settings section is gated on `mcp_oauth`; without the flag the
  // settings layout bounces /settings/mcp to the default section, so link only
  // when the destination actually exists for this org. See
  // `failClosedUntilFresh` on `useFeatureFlag` for why this must fail closed.
  const isMcpEnabled = useFeatureFlag('mcp_oauth', {
    orgId,
    failClosedUntilFresh: true,
  });

  const handleCopy = useCallback(() => {
    void copy(CLEANUP_PROMPT);
  }, [copy]);

  return (
    <AccordionItem className='rounded-lg border border-border-secondary bg-bg-secondary'>
      <AccordionTrigger className='w-full flex items-center gap-2 px-4 py-3 text-left'>
        <Stars01
          className='size-4 text-fg-brand-primary shrink-0'
          aria-hidden='true'
        />
        <span className='flex-1 text-sm font-medium text-fg-primary'>
          Prepare your data with AI
        </span>
        <AccordionChevron className='size-4 text-fg-quaternary shrink-0' />
      </AccordionTrigger>
      <AccordionPanel className='border-t border-border-secondary px-4 pb-4'>
        <p className='mt-3 text-sm text-fg-secondary'>
          Copy this prompt into ChatGPT, Claude, or your preferred AI assistant
          along with your spreadsheet. It will reconcile your data against your
          CRM and produce clean CSVs ready for import. Works best with the{' '}
          {isMcpEnabled ? (
            // New tab on purpose: this panel sits mid-import, and navigating in
            // place would discard any files already uploaded into the wizard.
            <a
              href={`/${orgId}/settings/mcp`}
              target='_blank'
              rel='noopener noreferrer'
              className='font-medium text-fg-brand-primary hover:underline'
            >
              Ahoy MCP server
            </a>
          ) : (
            'Ahoy MCP server'
          )}{' '}
          installed so your AI can read your CRM schema and search for
          duplicates directly.
        </p>
        <div className='mt-3 relative'>
          <pre
            className={cx(
              'whitespace-pre-wrap text-xs text-fg-secondary',
              'rounded-lg border border-border-secondary bg-bg-primary p-4 pr-12',
              'max-h-60 overflow-y-auto'
            )}
          >
            {CLEANUP_PROMPT}
          </pre>
          <div className='absolute right-2 top-2'>
            <Button
              color='secondary'
              size='xs'
              onClick={handleCopy}
              iconLeading={copied ? Check : Copy01}
              aria-label={copied ? 'Copied' : 'Copy prompt'}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
});
